import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DepartmentComplianceTable = ({ departments, onExport, onViewAudit }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'score', direction: 'desc' });
  const [searchTerm, setSearchTerm] = useState('');

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-success';
    if (score >= 75) return 'text-warning';
    return 'text-error';
  };

  const getScoreBadge = (score) => {
    if (score >= 90) return { bg: 'bg-success/10', text: 'text-success', label: 'Compliant' };
    if (score >= 75) return { bg: 'bg-warning/10', text: 'text-warning', label: 'Warning' };
    return { bg: 'bg-error/10', text: 'text-error', label: 'Critical' };
  };

  const sortedDepartments = [...departments]?.filter(dept => 
      dept?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      dept?.manager?.toLowerCase()?.includes(searchTerm?.toLowerCase())
    )?.sort((a, b) => {
      const aValue = a?.[sortConfig?.key];
      const bValue = b?.[sortConfig?.key];
      const modifier = sortConfig?.direction === 'asc' ? 1 : -1;
      return aValue > bValue ? modifier : -modifier;
    });

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden w-full">
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-semibold text-base md:text-lg text-foreground mb-1 break-words">
              Department Compliance Scores
            </h3>
            <p className="caption text-xs md:text-sm text-muted-foreground break-words">
              Real-time compliance monitoring across all departments
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <div className="relative flex-1 sm:flex-initial">
              <Icon 
                name="Search" 
                size={16} 
                color="var(--color-muted-foreground)" 
                className="absolute left-3 top-1/2 -translate-y-1/2"
              />
              <input
                type="text"
                placeholder="Search departments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full sm:w-64 pl-10 pr-4 py-2 text-sm bg-muted border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={onExport}
              iconName="Download"
              iconPosition="left"
              className="flex-shrink-0"
            >
              Export
            </Button>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[600px]">
          <thead className="bg-muted">
            <tr>
              <th 
                className="px-4 py-3 text-left cursor-pointer hover:bg-muted/80 transition-smooth"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center gap-2">
                  <span className="caption text-xs font-medium text-foreground">Department</span>
                  <Icon 
                    name={sortConfig?.key === 'name' ? (sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} 
                    size={14} 
                    color="var(--color-muted-foreground)" 
                  />
                </div>
              </th>
              <th className="px-4 py-3 text-left hidden md:table-cell">
                <span className="caption text-xs font-medium text-foreground">Manager</span>
              </th>
              <th 
                className="px-4 py-3 text-center cursor-pointer hover:bg-muted/80 transition-smooth"
                onClick={() => handleSort('score')}
              >
                <div className="flex items-center justify-center gap-2">
                  <span className="caption text-xs font-medium text-foreground">Score</span>
                  <Icon 
                    name={sortConfig?.key === 'score' ? (sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} 
                    size={14} 
                    color="var(--color-muted-foreground)" 
                  />
                </div>
              </th>
              <th 
                className="px-4 py-3 text-center cursor-pointer hover:bg-muted/80 transition-smooth hidden lg:table-cell"
                onClick={() => handleSort('violations')}
              >
                <div className="flex items-center justify-center gap-2">
                  <span className="caption text-xs font-medium text-foreground">Violations</span>
                  <Icon 
                    name={sortConfig?.key === 'violations' ? (sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} 
                    size={14} 
                    color="var(--color-muted-foreground)" 
                  />
                </div>
              </th>
              <th className="px-4 py-3 text-center hidden lg:table-cell">
                <span className="caption text-xs font-medium text-foreground">Status</span>
              </th>
              <th className="px-4 py-3 text-right">
                <span className="caption text-xs font-medium text-foreground">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedDepartments?.map((dept) => {
              const badge = getScoreBadge(dept?.score);
              return (
                <tr key={dept?.id} className="hover:bg-muted/50 transition-smooth">
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-md flex items-center justify-center flex-shrink-0">
                        <Icon name="Building2" size={20} color="var(--color-primary)" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-body font-medium text-sm text-foreground truncate">
                          {dept?.name}
                        </p>
                        <p className="caption text-xs text-muted-foreground md:hidden">
                          {dept?.manager}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 hidden md:table-cell">
                    <p className="caption text-sm text-foreground">{dept?.manager}</p>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <span className={`data-text text-lg font-bold ${getScoreColor(dept?.score)}`}>
                      {dept?.score}%
                    </span>
                  </td>
                  <td className="px-4 py-4 text-center hidden lg:table-cell">
                    <span className={`data-text text-sm font-medium ${dept?.violations > 0 ? 'text-error' : 'text-success'}`}>
                      {dept?.violations}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-center hidden lg:table-cell">
                    <span className={`${badge?.bg} ${badge?.text} px-3 py-1 text-xs font-caption rounded-full inline-block`}>
                      {badge?.label}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewAudit(dept?.id)}
                      iconName="FileText"
                      iconPosition="left"
                      className="text-xs"
                    >
                      <span className="hidden md:inline">Audit</span>
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {sortedDepartments?.length === 0 && (
        <div className="p-8 text-center">
          <Icon name="Search" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3 opacity-50" />
          <p className="caption text-sm text-muted-foreground">No departments found matching your search</p>
        </div>
      )}
    </div>
  );
};

export default DepartmentComplianceTable;